import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { LanguageProvider } from "@/lib/i18n";
import Welcome from "@/pages/Welcome";
import Register from "@/pages/Register";
import CustomerLayout from "@/pages/customer/Layout";
import CustomerDashboard from "@/pages/customer/Dashboard";
import Fertilizer from "@/pages/customer/Fertilizer";
import OrderReview from "@/pages/customer/OrderReview";
import MyOrders from "@/pages/customer/MyOrders";
import Profile from "@/pages/customer/Profile";
import Pesticide from "@/pages/customer/Pesticide";
import AdminLayout from "@/pages/admin/Layout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminOrders from "@/pages/admin/Orders";
import AdminCatalog from "@/pages/admin/Catalog";
import AdminCustomers from "@/pages/admin/Customers";
import AdminSettings from "@/pages/admin/Settings";
import AdminReports from "@/pages/admin/Reports";
import AdminBilling from "@/pages/admin/Billing";
import AdminAnnualReturns from "@/pages/admin/AnnualReturns";
import StockAdmin from "@/pages/admin/StockAdmin";
import StockAudit from "@/pages/admin/StockAudit";
import BusinessContent from "@/pages/admin/BusinessContent";
import BackupExport from "@/pages/admin/BackupExport";

function Protected({ role, children }) {
  const { user, role: currentRole } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (role && currentRole !== role) {
    return <Navigate to={currentRole === "admin" ? "/admin" : "/customer"} replace />;
  }
  return children;
}

function HomeRoute() { const { user } = useAuth(); if (user?.role === "customer") return <Navigate to="/customer" replace />; if (user?.role === "admin" && localStorage.getItem("sf_token")) return <Navigate to="/admin" replace />; return <Welcome />; }

function MasterOnly({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "admin" || !user.is_master) return <Navigate to="/admin" replace />;
  return children;
}

function StockAdminOnly({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "admin" || user.admin_type !== "stock_admin") {
    return <Navigate to="/admin" replace />;
  }
  return children;
}


function OpeningAnimation({ children }) {
  const [show, setShow] = useState(() => {
    return !sessionStorage.getItem("sf_opening_animation_shown");
  });
  const [splashImage, setSplashImage] = useState(null);

  useEffect(() => {
    if (!show) return;

    let active = true;

    api.get("/public/opening-animation")
      .then(({ data }) => {
        if (active && data?.opening_animation_url) {
          setSplashImage(data.opening_animation_url);
        }
      })
      .catch(() => {});

    const timer = setTimeout(() => {
      sessionStorage.setItem("sf_opening_animation_shown", "1");
      setShow(false);
    }, 4800);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [show]);

  const skipAnimation = () => {
    sessionStorage.setItem("sf_opening_animation_shown", "1");
    setShow(false);
  };

  if (!show) return children;

  return (
    <>
      <style>{`
        .sf-premium {
          position: fixed;
          inset: 0;
          z-index: 99999;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background:
            linear-gradient(
              180deg,
              rgba(7,29,44,.18),
              rgba(25,82,39,.20)
            ),
            #dbead1;
          background-image:
            linear-gradient(
              180deg,
              rgba(7,29,44,.12),
              rgba(25,82,39,.16)
            ),
            var(--sf-splash-image, none);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          color: #fff;
          isolation: isolate;
        }

        .sf-premium-sky {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(
              circle at 50% 38%,
              rgba(255,238,174,.95) 0,
              rgba(255,196,92,.48) 12%,
              rgba(255,153,62,.12) 28%,
              transparent 47%
            ),
            linear-gradient(
              180deg,
              rgba(20,94,123,.3),
              transparent 55%
            );
          animation: sf-sky 4.8s ease-out both;
        }

        .sf-sun {
          position: absolute;
          width: clamp(90px, 15vw, 190px);
          aspect-ratio: 1;
          left: 50%;
          top: 39%;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          background: #ffe9a3;
          box-shadow:
            0 0 35px rgba(255,231,155,.9),
            0 0 100px rgba(255,183,75,.5),
            0 0 180px rgba(255,159,53,.25);
          animation: sf-sun-rise 4.8s ease-out both;
        }

        .sf-horizon {
          position: absolute;
          left: -5%;
          right: -5%;
          bottom: 25%;
          height: 28%;
          background:
            linear-gradient(
              155deg,
              transparent 0 34%,
              rgba(19,69,47,.72) 34.5% 48%,
              transparent 48.5%
            ),
            linear-gradient(
              25deg,
              transparent 0 39%,
              rgba(26,82,48,.78) 39.5% 55%,
              transparent 55.5%
            );
          filter: blur(.4px);
          transform: scale(1.06);
          animation: sf-horizon 4.8s ease-out both;
        }

        .sf-field {
          position: absolute;
          left: -8%;
          right: -8%;
          bottom: -8%;
          height: 43%;
          background:
            repeating-linear-gradient(
              82deg,
              rgba(25,82,39,.98) 0 7px,
              rgba(47,118,48,.96) 7px 15px,
              rgba(88,148,51,.9) 15px 20px,
              rgba(31,98,42,.98) 20px 29px
            );
          transform: perspective(500px) rotateX(8deg) scale(1.08);
          transform-origin: bottom center;
          box-shadow: 0 -20px 55px rgba(22,71,37,.3);
          animation: sf-field 4.8s ease-out both;
        }

        .sf-field-glow {
          position: absolute;
          left: -15%;
          right: -15%;
          bottom: 16%;
          height: 16%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255,232,164,.22),
            transparent
          );
          filter: blur(14px);
          animation: sf-glow 4.8s ease-in-out infinite;
        }

        .sf-mist {
          position: absolute;
          left: -15%;
          right: -15%;
          bottom: 25%;
          height: 20%;
          background:
            radial-gradient(
              ellipse at 50% 50%,
              rgba(255,244,205,.24),
              transparent 65%
            );
          filter: blur(18px);
          animation: sf-mist 4.8s ease-in-out infinite alternate;
        }

        .sf-particle {
          position: absolute;
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: rgba(255,246,205,.8);
          box-shadow: 0 0 9px rgba(255,235,160,.9);
          animation: sf-particle 4.8s linear infinite;
        }

        .sf-p1 { left: 18%; top: 60%; animation-delay: -.5s; }
        .sf-p2 { left: 30%; top: 35%; animation-delay: -2s; }
        .sf-p3 { left: 69%; top: 29%; animation-delay: -1.4s; }
        .sf-p4 { left: 82%; top: 57%; animation-delay: -3s; }
        .sf-p5 { left: 57%; top: 68%; animation-delay: -2.6s; }
        .sf-p6 { left: 43%; top: 24%; animation-delay: -3.6s; }

        .sf-leaf {
          position: absolute;
          width: clamp(20px, 3vw, 38px);
          height: clamp(9px, 1.4vw, 16px);
          border-radius: 100% 0 100% 0;
          background: linear-gradient(135deg, #d8ed8c, #4c9c49);
          opacity: .7;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,.12));
          animation: sf-leaf-float 4.8s linear infinite;
        }

        .sf-l1 { left: 8%; top: 45%; animation-delay: -.7s; }
        .sf-l2 { left: 78%; top: 35%; animation-delay: -2.4s; }
        .sf-l3 { left: 88%; top: 64%; animation-delay: -1.6s; }
        .sf-l4 { left: 12%; top: 70%; animation-delay: -3.4s; }

        .sf-content {
          position: relative;
          z-index: 10;
          width: min(92vw, 760px);
          text-align: center;
          padding: 30px 20px;
          animation: sf-content-in 1.15s cubic-bezier(.2,.8,.2,1) both;
        }

        .sf-mark {
          position: relative;
          width: clamp(92px, 14vw, 145px);
          height: clamp(92px, 14vw, 145px);
          margin: 0 auto 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sf-mark-ring {
          position: absolute;
          inset: 0;
          border: 1px solid rgba(255,244,205,.65);
          border-radius: 50%;
          box-shadow:
            0 0 30px rgba(255,230,157,.2),
            inset 0 0 25px rgba(255,255,255,.12);
          animation: sf-ring 4.8s ease-in-out infinite;
        }

        .sf-mark-core {
          width: 62%;
          height: 62%;
          border-radius: 50%;
          background:
            radial-gradient(circle at 38% 30%, #f8f0bb, #d4aa4e 48%, #507e3d 50%, #245f38 100%);
          box-shadow:
            0 12px 35px rgba(0,0,0,.24),
            0 0 35px rgba(255,221,133,.28);
          position: relative;
          overflow: hidden;
        }

        .sf-mark-core::before,
        .sf-mark-core::after {
          content: "";
          position: absolute;
          width: 34%;
          height: 52%;
          background: #e5f0a3;
          border-radius: 100% 0 100% 0;
          top: 22%;
        }

        .sf-mark-core::before {
          left: 23%;
          transform: rotate(-28deg);
        }

        .sf-mark-core::after {
          right: 18%;
          transform: rotate(32deg) scale(.82);
          background: #9ccf68;
        }

        .sf-title {
          margin: 0;
          font-size: clamp(36px, 7vw, 72px);
          line-height: .95;
          font-weight: 900;
          letter-spacing: .075em;
          color: #fffdf2;
          text-shadow:
            0 3px 20px rgba(0,0,0,.3),
            0 0 30px rgba(255,224,139,.2);
          animation: sf-title 1.4s .2s cubic-bezier(.2,.8,.2,1) both;
        }

        .sf-title span {
          color: #f2c75d;
        }

        .sf-subtitle {
          margin-top: 13px;
          font-size: clamp(12px, 2vw, 18px);
          letter-spacing: .34em;
          text-transform: uppercase;
          font-weight: 700;
          color: rgba(255,250,225,.88);
          animation: sf-subtitle 1.2s .55s ease both;
        }

        .sf-line {
          width: min(54vw, 250px);
          height: 1px;
          margin: 20px auto 0;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255,238,174,.9),
            transparent
          );
          animation: sf-line 1.4s .7s ease both;
        }

        .sf-tagline {
          margin-top: 15px;
          font-size: clamp(11px, 1.8vw, 16px);
          color: rgba(255,250,230,.76);
          letter-spacing: .04em;
          animation: sf-subtitle 1.2s .8s ease both;
        }

        .sf-progress {
          width: min(66vw, 340px);
          height: 3px;
          margin: 30px auto 0;
          background: rgba(255,255,255,.18);
          overflow: hidden;
          border-radius: 99px;
        }

        .sf-progress-bar {
          width: 0;
          height: 100%;
          background: linear-gradient(90deg, #b9dc72, #f6d06c);
          box-shadow: 0 0 12px rgba(246,208,108,.75);
          animation: sf-progress 4.8s linear forwards;
        }

        .sf-loading {
          margin-top: 10px;
          font-size: 10px;
          letter-spacing: .22em;
          text-transform: uppercase;
          color: rgba(255,248,225,.6);
        }

        .sf-skip {
          position: absolute;
          z-index: 30;
          top: max(18px, env(safe-area-inset-top));
          right: max(18px, env(safe-area-inset-right));
          min-width: 70px;
          min-height: 42px;
          border: 1px solid rgba(255,255,255,.28);
          border-radius: 999px;
          padding: 9px 16px;
          background: rgba(8,40,48,.35);
          color: rgba(255,255,255,.9);
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          transition: background .2s ease, transform .2s ease;
        }

        .sf-skip:hover {
          background: rgba(255,255,255,.16);
          transform: translateY(-1px);
        }

        @keyframes sf-sky {
          from { opacity: .55; transform: scale(1.04); }
          to { opacity: 1; transform: scale(1); }
        }

        @keyframes sf-sun-rise {
          from {
            opacity: 0;
            transform: translate(-50%, -43%) scale(.72);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        @keyframes sf-horizon {
          from { transform: scale(1.13) translateY(15px); opacity: .2; }
          to { transform: scale(1.06) translateY(0); opacity: 1; }
        }

        @keyframes sf-field {
          from { transform: perspective(500px) rotateX(8deg) scale(1.12) translateX(-2%); }
          to { transform: perspective(500px) rotateX(8deg) scale(1.08) translateX(2%); }
        }

        @keyframes sf-glow {
          0%, 100% { transform: translateX(-35%); opacity: .25; }
          50% { transform: translateX(35%); opacity: .7; }
        }

        @keyframes sf-mist {
          from { transform: translateX(-3%) scale(1); opacity: .35; }
          to { transform: translateX(3%) scale(1.05); opacity: .65; }
        }

        @keyframes sf-particle {
          0% { transform: translate3d(0,25px,0) scale(.5); opacity: 0; }
          25% { opacity: .8; }
          70% { opacity: .65; }
          100% { transform: translate3d(18px,-120px,0) scale(1.1); opacity: 0; }
        }

        @keyframes sf-leaf-float {
          0% {
            transform: translate3d(-10px,20px,0) rotate(-18deg);
            opacity: 0;
          }
          15% { opacity: .7; }
          50% {
            transform: translate3d(45px,-35px,0) rotate(95deg);
            opacity: .8;
          }
          100% {
            transform: translate3d(110px,-110px,0) rotate(180deg);
            opacity: 0;
          }
        }

        @keyframes sf-content-in {
          from { opacity: 0; transform: translateY(22px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes sf-title {
          from { opacity: 0; transform: translateY(12px); letter-spacing: .16em; }
          to { opacity: 1; transform: translateY(0); letter-spacing: .075em; }
        }

        @keyframes sf-subtitle {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes sf-line {
          from { transform: scaleX(0); opacity: 0; }
          to { transform: scaleX(1); opacity: 1; }
        }

        @keyframes sf-ring {
          0%, 100% { transform: scale(.9) rotate(0deg); opacity: .45; }
          50% { transform: scale(1.04) rotate(180deg); opacity: .9; }
        }

        @keyframes sf-progress {
          from { width: 0; }
          to { width: 100%; }
        }

        @media (max-width: 600px) {
          .sf-content {
            padding: 72px 16px 24px;
          }

          .sf-mark {
            margin-bottom: 15px;
          }

          .sf-subtitle {
            letter-spacing: .2em;
          }

          .sf-tagline {
            max-width: 310px;
            margin-left: auto;
            margin-right: auto;
          }

          .sf-field {
            height: 36%;
          }

          .sf-horizon {
            bottom: 23%;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .sf-premium *,
          .sf-premium::before,
          .sf-premium::after {
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>

      <div
        className="sf-premium"
        style={splashImage ? { "--sf-splash-image": `url("${splashImage}")` } : undefined}
      >
        <div className="sf-premium-sky" />
        <div className="sf-sun" />
        <div className="sf-horizon" />
        <div className="sf-mist" />
        <div className="sf-field" />
        <div className="sf-field-glow" />

        <div className="sf-particle sf-p1" />
        <div className="sf-particle sf-p2" />
        <div className="sf-particle sf-p3" />
        <div className="sf-particle sf-p4" />
        <div className="sf-particle sf-p5" />
        <div className="sf-particle sf-p6" />

        <div className="sf-leaf sf-l1" />
        <div className="sf-leaf sf-l2" />
        <div className="sf-leaf sf-l3" />
        <div className="sf-leaf sf-l4" />

        <button
          type="button"
          className="sf-skip"
          onClick={skipAnimation}
          aria-label="Skip opening animation"
        >
          Skip ›
        </button>

        <div className="sf-content">
          <div className="sf-mark">
            <div className="sf-mark-ring" />
            <div className="sf-mark-core" />
          </div>

          <h1 className="sf-title">
            SABUJ <span>SONA</span>
          </h1>

          <div className="sf-subtitle">Fertilizer Portal</div>
          <div className="sf-line" />

          <div className="sf-tagline">
            Growing a greener future, together.
          </div>

          <div className="sf-progress">
            <div className="sf-progress-bar" />
          </div>

          <div className="sf-loading">
            Preparing your portal
          </div>
        </div>
      </div>
    </>
  );
}

export default function App() {
  return (
    <OpeningAnimation>
      <AuthProvider>
      <LanguageProvider>
      <CartProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/customer"
              element={
                <Protected role="customer">
                  <CustomerLayout />
                </Protected>
              }
            >
              <Route index element={<CustomerDashboard />} />
              <Route path="fertilizer" element={<Fertilizer />} />
              <Route path="review" element={<OrderReview />} />
              <Route path="pesticide" element={<Pesticide />} />
              <Route path="orders" element={<MyOrders />} />
              <Route path="profile" element={<Profile />} />
            </Route>
            <Route
              path="/admin"
              element={
                <Protected role="admin">
                  <AdminLayout />
                </Protected>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
             <Route path="billing" element={<AdminBilling />} />
              <Route path="annual-returns" element={<AdminAnnualReturns />} />
              <Route path="stock" element={<StockAdminOnly><StockAdmin /></StockAdminOnly>} />
              <Route path="stock-audit" element={<MasterOnly><StockAudit /></MasterOnly>} />
              <Route path="catalog" element={<AdminCatalog />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="business-content" element={<MasterOnly><BusinessContent /></MasterOnly>} />
              <Route path="backup" element={<MasterOnly><BackupExport /></MasterOnly>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </CartProvider>
      </LanguageProvider>
      </AuthProvider>
    </OpeningAnimation>
  );
}
